/**
 * Seeded PRNG (mulberry32). Deterministic RNG = reproducible bugs = testable
 * (TECHNICAL_APPROACH §5). No bare Math.random() anywhere in core. The seed and
 * cursor live in the save so a reload reproduces the exact stream.
 */
export interface RngState {
  seed: number;
  cursor: number;
}

export function createRng(seed: number): RngState {
  return { seed: seed >>> 0, cursor: 0 };
}

function mulberry32(a: number): number {
  let t = (a += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** Returns a float in [0,1) and advances the cursor (mutates state). */
export function nextFloat(rng: RngState): number {
  rng.cursor = (rng.cursor + 1) >>> 0;
  return mulberry32((rng.seed ^ Math.imul(rng.cursor, 0x9e3779b1)) >>> 0);
}

/** Integer in [min, max] inclusive. */
export function nextInt(rng: RngState, min: number, max: number): number {
  return min + Math.floor(nextFloat(rng) * (max - min + 1));
}

/** True with probability p. */
export function chance(rng: RngState, p: number): boolean {
  return nextFloat(rng) < p;
}

/** Weighted pick: returns the index, given an array of positive weights. */
export function weightedIndex(rng: RngState, weights: readonly number[]): number {
  const total = weights.reduce((a, b) => a + b, 0);
  let r = nextFloat(rng) * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i] ?? 0;
    if (r < 0) return i;
  }
  return weights.length - 1;
}
